import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Divider, Flex, TableColumnsType,
} from 'antd';
import {
    useEffect,
    useMemo, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import {
    usersMutations, usersQueries,
} from '@/api';
import {
    ActionDeleteButton,
    ActionEditButton,
    ActionsButtonWrapper,
    ActionViewButton,
    CreateButton,
    DefaultTable,
    DeleteProjectModal,
    RoleGuard,
} from '@/components';
import {
    AppRoute, DEFAULT_ORDER_COLUMN, DEFAULT_ORDER_DIRECTION, UserRole,
} from '@/constants';
import { useCustomParams } from '@/hooks';
import { useUserStore } from '@/stores';
import {
    DefaultUsersOrderColumn,
    SortDirection,
    User,
} from '@/types';
import {
    canDeleteUser, canEditUser, getActionsColumn, getCreatedAtColumn, getNameColumn, getNicknameColumn, getProjectsAndScriptsCountersColumn, getRoleColumn,
} from '@/utils';

const FlexInlineStyle: React.CSSProperties = {
    display: 'inline-flex',
};

export const UsersPage = () => {
    const navigate = useNavigate();
    const { user: currentUser } = useUserStore();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const { orderColumn, orderDirection, currentPage, pageSize, search, setOrderColumn, setOrderDirection  } = useCustomParams({ defaultPageSize: 100 });

    useEffect(() => {
        if (!orderColumn || orderColumn === 'id' || orderColumn === DEFAULT_ORDER_COLUMN) {
            setOrderColumn('nickname');
        }
        if (!orderDirection || orderDirection === DEFAULT_ORDER_DIRECTION) {
            setOrderDirection('asc');
        }
    }, [orderColumn, orderDirection, setOrderColumn, setOrderDirection]);

    const { data, isFetching } = useQuery(usersQueries.getAllUsers({
        per_page: pageSize,
        current_page: currentPage,
        order_column: orderColumn,
        order_direction: orderDirection,
        search: search,
    }));

    const deleteMutationOptions = useMemo(
        () => userToDelete
            ? usersMutations.delete(userToDelete.id)
            : usersMutations.delete(''),
        [userToDelete],
    );

    const { mutate: deleteUser, isPending: isDeleting } = useMutation(deleteMutationOptions);

    const handleDeleteConfirm = () => {
        if (userToDelete) {
            deleteUser(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setUserToDelete(null);
                },
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setUserToDelete(null);
    };

    const columns: TableColumnsType<User> = useMemo(() => [
        getNameColumn(),
        getNicknameColumn((record) => navigate(`${AppRoute.Users}/${record.id}`)),
        getCreatedAtColumn(),
        getRoleColumn(),
        getProjectsAndScriptsCountersColumn((_: unknown, record: User) => (
            record.role === UserRole.Manager ? (
                <Flex align="center" wrap={false} style={FlexInlineStyle}>
                    <span>{record.projects.length || 0}</span>
                    <Divider type="vertical" />
                    <span>{record.scripts.length || 0}</span>
                    <Divider type="vertical" />
                    <ActionEditButton
                        onClick={() => navigate(`${AppRoute.Users}/${record.id}/projects`)}
                        to={`${AppRoute.Users}/${record.id}/projects`}
                    />
                </Flex>
            ) : null
        )),
        getActionsColumn({
            render: (_: unknown, record: User) => {
                const handleDelete = () => {
                    setUserToDelete(record);
                    setDeleteModalOpen(true);
                };

                return (
                    <ActionsButtonWrapper>
                        <ActionViewButton
                            onClick={() => navigate(`${AppRoute.Users}/${record.id}`)}
                            to={`${AppRoute.Users}/${record.id}`}
                        />
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                            <ActionsButtonWrapper>
                                {canEditUser(currentUser, record) && (
                                    <ActionEditButton
                                        onClick={() => navigate(`${AppRoute.Users}/${record.id}/edit`)}
                                        to={`${AppRoute.Users}/${record.id}/edit`}
                                    />
                                )}
                                {canDeleteUser(currentUser, record) && (
                                    <ActionDeleteButton onClick={handleDelete} />
                                )}
                            </ActionsButtonWrapper>
                        </RoleGuard>
                    </ActionsButtonWrapper>
                );
            },
        }),
    ], [navigate, currentUser]);

    return (
        <>
            <Helmet>
                <title>Користувачі</title>
            </Helmet>

            <DefaultTable
                title="Користувачі"
                defaultPageSize={100}
                columns={columns}
                dataSource={data?.items || []}
                total={data?.total || 0}
                loading={isFetching}
                renderCreateButton={() => (
                    <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin]}>
                        <CreateButton
                            label="Створити користувача"
                            disabled={isFetching}
                            to={AppRoute.UserCreate}
                        />
                    </RoleGuard>
                )}
            />

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={userToDelete?.nickname || userToDelete?.email}
                entityType="user"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />
        </>
    );
};
